import React, {
  ChangeEvent,
  Dispatch,
  MutableRefObject,
  SetStateAction,
  useRef,
  useState,
} from "react";
import FormControl from "@mui/joy/FormControl";
import FormLabel from "@mui/joy/FormLabel";
import Stack from "@mui/joy/Stack";
import CardActions from "@mui/joy/CardActions";
import CardOverflow from "@mui/joy/CardOverflow";
import "react-international-phone/style.css";
import { add, update } from "../../service/industryService";
import { Button } from "primereact/button";
import DropdownApi from "../../components/DropdownApi";
import { rolesAdd, rolesUpdate } from "../../service/rolesService";
import { InputSwitch } from "primereact/inputswitch";
import MultiDropdownApi from "../../components/MultiDropdownApi";
import { MultiSelect } from "primereact/multiselect";
import { addDocument, updateDocument } from "../../service/documentsService";
import { InputTextarea } from "primereact/inputtextarea";
import { Input, Textarea } from "../../components/Shared/Forms";

interface inputProps {
  id: string;
  record: any;
  selectIndustry?: boolean;
  selectAccessLevel?: boolean;
  selectAppointment?: boolean;
  selectEquipment?: boolean;
  selectVehicle?: boolean;
  selectChemical?: boolean;
  toast: MutableRefObject<any>;
  setGlobalLoader: Dispatch<SetStateAction<boolean>>;
  setVisible: Dispatch<SetStateAction<boolean>>;
  setReload: Dispatch<SetStateAction<boolean>>;
  reload: boolean;
  doc_type:
  | "client"
  | "consultant"
  | "employee"
  | "equipment"
  | "subcontractor"
  | "vehicle"
  | "chemical"
  | "site";
}

export default function DocumentsAdd(modalProps: inputProps) {
  const record = modalProps.record;

  console.log("modalProps record == ", record);

  const [industry, setIndustry] = useState(record?.industry || []);
  const [equipmentType, setEquipmentType] = useState(record?.equipment_type || []);
  const [vehicleType, setVehicleType] = useState(record?.vehicle_type || []);
  const [chemicalType, setChemicalType] = useState(record?.chemical_type || []);
  const [access, setAccess] = useState(record?.access_level || []);
  const [appointment, setAppointment] = useState(record?.appointment || []);
  const [required, setRequired] = useState(record?.required || false);
  const [nameError, setNameError] = useState(false);

  const [formData, setFormData] = useState<{
    name: string;
    description: string;
  }>({
    name: record?.name || "",
    description: record?.description || "",
  });
  const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
  const requiredFields = ["name", "description"];

  async function addIndustry() {
    const updateObject: any = {
      name: formData.name,
      required,
      doc_type: modalProps.doc_type,
      description: formData.description,
    };
    if (modalProps.selectAccessLevel) {
      updateObject.accessLevel = access;
    }
    if (modalProps.selectIndustry) {
      updateObject.industry = industry;
    }
    if (modalProps.selectAppointment) {
      updateObject.appointment = appointment;
    }
    if (modalProps.selectEquipment) {
      updateObject.equipment_type = equipmentType;
    }
    if (modalProps.selectVehicle) {
      updateObject.vehicle_type = vehicleType;
    }
    if (modalProps.selectChemical) {
      updateObject.chemical_type = chemicalType;
    }
    // Validate against required fields
    const errors: { [key: string]: string } = {};

    requiredFields.forEach(field => {
      const value: any = (updateObject as any)[field];
      const isEmpty =
        value === undefined ||
        value === null ||
        (typeof value === "string" && value.trim() === "") ||
        (Array.isArray(value) && value.length === 0);

      if (isEmpty) {
        errors[field] = "This field is required";
      }
    });

    console.log("Errors: ", errors);

    // Reflect errors in state and block submission if any exist
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      setNameError(!!errors.name);

      modalProps.toast.current.show({
        severity: "warn",
        summary: "Please enter all required fields",
        life: 3000,
      });
      return;
    } else {
      // Clear previous errors if validation passes
      setFieldErrors({});
      setNameError(false);
    }

    modalProps.setGlobalLoader(true);
    if (modalProps.id) {
      updateDocument(record.id, updateObject).then((r: any) => {
        modalProps.setVisible(false);
        modalProps.setGlobalLoader(false);
        modalProps.setReload(!modalProps.reload);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Document Updated Successfully",
          life: 3000,
        });
      });
    } else {
      addDocument(updateObject).then((r: any) => {
        modalProps.setVisible(false);
        modalProps.setGlobalLoader(false);
        modalProps.setReload(!modalProps.reload);
        modalProps.toast.current.show({
          severity: "success",
          summary: "Document Added Successfully",
          life: 3000,
        });
      });
    }
  }

  function handleInputChange(e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) {
    const { name, value } = e.target;

    // Clear any existing error for this field
    if (fieldErrors[name]) {
      setFieldErrors(prevErrors => {
        const newErrors = { ...prevErrors };
        delete newErrors[name];
        return newErrors;
      });
    }

    // @ts-ignore
    setFormData({
      ...formData,
      [name]: value,
    });
  }

  return (
    <React.Fragment>
      <Stack direction="row" spacing={3} sx={{ display: { xs: "none", md: "flex" }, my: 1 }}>
        <Stack spacing={2} sx={{ flexGrow: 1 }} style={{ width: "100%" }}>
          <Stack spacing={1}>
            <Input
              label={"Document Name"}
              placeholder={"Document name"}
              required={true}
              value={formData?.name}
              name={"name"}
              onChange={handleInputChange}
              error={fieldErrors?.name}
            />
          </Stack>
          <Stack spacing={1}>
            <Textarea
              label={"Description"}
              name={"description"}
              required={true}
              value={formData?.description}
              onChange={handleInputChange}
              placeholder={"Document description"}
              error={fieldErrors?.description}
            />
          </Stack>
          {modalProps.selectAccessLevel ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="access-level"
                optionLabel="name"
                optionValue="id"
                label="Access Level"
                selectedData={access}
                setSelectedData={setAccess}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          {modalProps.selectIndustry ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="industry"
                optionLabel="name"
                optionValue="id"
                label="Industry"
                selectedData={industry}
                setSelectedData={setIndustry}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          {modalProps.selectAppointment ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="appointments"
                optionLabel="name"
                optionValue="id"
                label="Appointment"
                selectedData={appointment}
                setSelectedData={setAppointment}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          {modalProps.selectEquipment ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="equipment-type"
                optionLabel="name"
                optionValue="id"
                label="Equipment Type"
                selectedData={equipmentType}
                setSelectedData={setEquipmentType}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          {modalProps.selectVehicle ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="vehicle-type"
                optionLabel="name"
                optionValue="id"
                label="Vehicle Type"
                selectedData={vehicleType}
                setSelectedData={setVehicleType}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          {modalProps.selectChemical ? (
            <Stack spacing={1}>
              <MultiDropdownApi
                apiPath="chemical-type"
                optionLabel="name"
                optionValue="id"
                label="Chemical Type"
                selectedData={chemicalType}
                setSelectedData={setChemicalType}
                showAll={true}
              />
            </Stack>
          ) : (
            ""
          )}
          <Stack spacing={1}>
            <FormLabel>Required</FormLabel>
            <FormControl
              error={nameError}
              sx={{ display: { sm: "flex-column", md: "flex-row" }, gap: 2 }}
            >
              <InputSwitch checked={required} onChange={e => setRequired(e.value)} />
            </FormControl>
          </Stack>
        </Stack>
      </Stack>
      <CardOverflow
        style={{ width: "100%" }}
        sx={{ borderTop: "1px solid", borderColor: "divider" }}
      >
        <CardActions style={{ marginTop: 15 }} sx={{ alignSelf: "flex-end", pt: 2 }}>
          <Button
            type="button"
            icon="pi pi-save"
            label="Save"
            className="p-button-outlined"
            size="small"
            style={{ position: "absolute", fontSize: "small", right: 0, color: "#2dd36f" }}
            onClick={() => addIndustry()}
          />
        </CardActions>
      </CardOverflow>
    </React.Fragment>
  );
}
